import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  #lastEventTimestamp = 0;
  /** @type {string} */
  #lastEventSourceId = '';
  /** @type {number} */
  #pollInterval = null;
  /** @type {number} */
  #lastFetchedCount = -1;

  /** @type {HTMLElement} */
  get visuallyHiddenSpan() {
    return this.refs.cartBubbleText?.querySelector('.visually-hidden');
  }

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    const displayValue = value < 100 ? String(value) : '';
    this.refs.cartBubbleCount.textContent = displayValue;
    
    // Also update the visually-hidden accessibility text
    if (this.visuallyHiddenSpan) {
      // Extract the label part (everything before the colon) and update the count
      const currentText = this.visuallyHiddenSpan.textContent || '';
      const colonIndex = currentText.indexOf(':');
      
      if (colonIndex !== -1) {
        // Keep the label part and update the count
        const label = currentText.substring(0, colonIndex + 1).trim();
        this.visuallyHiddenSpan.textContent = `${label} ${value}`;
      } else {
        // Fallback if format is unexpected
        this.visuallyHiddenSpan.textContent = `Total items in cart: ${value}`;
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    
    // Listen for cart drawer close events (Boost app or native drawer)
    document.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    
    // Start optimized polling for cart changes (for Boost app compatibility)
    this.#startPolling();
    
    // Initial fetch to sync with actual cart
    this.#syncCartCount();
    
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    
    this.#stopPolling();
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Fetches the actual cart count from Shopify API.
   * This is the authoritative source for cart count.
   */
  #fetchActualCartCount = async () => {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;
      
      const cart = await response.json();
      return cart.item_count || 0;
    } catch (error) {
      return null;
    }
  };

  /**
   * Syncs the cart count with the actual cart from Shopify API.
   */
  #syncCartCount = async () => {
    const actualCount = await this.#fetchActualCartCount();
    if (actualCount !== null && actualCount !== this.#lastFetchedCount) {
      this.#lastFetchedCount = actualCount;
      const currentDisplayed = this.currentCartCount;
      
      // Only update if there's a discrepancy
      if (actualCount !== currentDisplayed) {
        this.renderCartBubble(actualCount, false, false);
      }
    }
  };

  /**
   * Debounced sync to prevent too many API calls.
   */
  #debouncedSync = null;

  /**
   * Starts optimized polling for cart changes.
   * Uses longer intervals and only polls when page is visible and active.
   * Polling is a fallback for Boost app compatibility - most updates come from events.
   */
  #startPolling = () => {
    // Debounce sync calls
    let syncTimeout = null;
    this.#debouncedSync = () => {
      clearTimeout(syncTimeout);
      syncTimeout = setTimeout(() => {
        if (document.visibilityState === 'visible') {
          this.#syncCartCount();
        }
      }, 500);
    };

    // Poll every 15 seconds (optimized for performance)
    // Only when page is visible and user is active
    this.#pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible' && !document.hidden) {
        this.#syncCartCount();
      }
    }, 15000); // 15 seconds - reduced frequency for better performance

    // Also sync when page becomes visible (user returns to tab)
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    
    // Sync when window gets focus (user switches back to tab)
    window.addEventListener('focus', this.#debouncedSync);
  };

  /**
   * Handles visibility change events.
   */
  #onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // Sync immediately when page becomes visible
      this.#syncCartCount();
    }
  };

  /**
   * Handles dialog close events - syncs cart when cart drawer closes.
   */
  #onDialogClose = (event) => {
    // Check if the closed dialog is a cart drawer
    const dialog = event.target;
    if (dialog?.closest('cart-drawer-component') || dialog?.closest('.cart-drawer')) {
      // Sync cart count when drawer closes (user might have modified cart in Boost app)
      this.#syncCartCount();
    }
  };

  /**
   * Stops polling for cart changes.
   */
  #stopPolling = () => {
    if (this.#pollInterval) {
      clearInterval(this.#pollInterval);
      this.#pollInterval = null;
    }
    
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    if (this.#debouncedSync) {
      window.removeEventListener('focus', this.#debouncedSync);
      this.#debouncedSync = null;
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const sourceId = event.detail.sourceId || '';
    const eventTimestamp = Date.now();

    // Prevent duplicate events from the same source within 100ms
    if (
      this.#lastEventSourceId === sourceId &&
      eventTimestamp - this.#lastEventTimestamp < 100
    ) {
      return;
    }

    this.#lastEventSourceId = sourceId;
    this.#lastEventTimestamp = eventTimestamp;
    
    // Always fetch the actual cart count to ensure accuracy (especially for Boost app)
    const actualCount = await this.#fetchActualCartCount();
    
    if (actualCount !== null) {
      // Use the actual count from the API as the source of truth
      this.#lastFetchedCount = actualCount;
      this.renderCartBubble(actualCount, false);
    } else {
      // Fallback to event data if fetch fails
      const itemCount = event.detail.data?.itemCount ?? 0;
      const comingFromProductForm = event.detail.data?.source === 'product-form-component';
      
      // Try to get count from resource sections if available
      const resource = event.detail.resource;
      if (resource?.sections) {
        for (const [sectionId, sectionHTML] of Object.entries(resource.sections)) {
          if (sectionHTML) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHTML, 'text/html');
            const cartItemCountElement = doc.querySelector('[ref="cartItemCount"]');
            if (cartItemCountElement) {
              const countFromSection = parseInt(cartItemCountElement.textContent || '0', 10);
              if (!isNaN(countFromSection) && countFromSection >= 0) {
                this.renderCartBubble(countFromSection, false);
                return;
              }
            }
          }
        }
      }
      
      // Last resort: use itemCount from event
      this.renderCartBubble(itemCount, comingFromProductForm);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // itemCount should always be the total count from the cart
    // comingFromProductForm is kept for backward compatibility but we now always use itemCount as total
    
    // Ensure itemCount is never negative
    const newCount = Math.max(0, itemCount);

    this.refs.cartBubbleCount.classList.toggle('hidden', newCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', newCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', newCount > 0 && animate);

    // Always set directly to the provided count (which should be the total from API or event)
    this.currentCartCount = newCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', newCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
